import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Bot, Send, Loader2, ChevronLeft, Sparkles, Brain, Search, FileText,
  AlertCircle, Info, CheckCircle, XCircle, ExternalLink, RefreshCw,
  MessageSquare, Zap, Target, TrendingUp, Settings, User, Clock, ThumbsUp, ThumbsDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";
import { format } from "date-fns";

// Thinking step icons
const ThinkingStepIcon = ({ step }) => {
  if (step.includes("Analyzing")) return <Search className="h-4 w-4 text-primary" />;
  if (step.includes("Gathering") || step.includes("Found")) return <FileText className="h-4 w-4 text-primary" />;
  if (step.includes("Ranking") || step.includes("Selected")) return <Target className="h-4 w-4 text-primary" />;
  if (step.includes("Generating") || step.includes("ready")) return <Sparkles className="h-4 w-4 text-primary" />;
  return <Brain className="h-4 w-4 text-primary" />;
};

// Message bubble component
const MessageBubble = ({ message, isUser, onFeedback }) => {
  const [feedbackGiven, setFeedbackGiven] = useState(message.feedback_rating || null);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  const handleFeedback = async (rating) => {
    if (feedbackGiven || !message.query_id) return;
    setIsSubmittingFeedback(true);
    try {
      await onFeedback(message.query_id, rating);
      setFeedbackGiven(rating);
    } catch (err) {
      // Error handled by parent
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-4`}>
      <div className={`max-w-[80%] ${isUser ? "order-2" : "order-1"}`}>
        <div className="flex items-start gap-2">
          {!isUser && (
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Bot className="h-4 w-4 text-primary" />
            </div>
          )}
          <div
            className={`rounded-2xl px-4 py-3 ${
              isUser
                ? "bg-primary text-primary-foreground rounded-br-md"
                : "bg-muted rounded-bl-md"
            }`}
            data-testid={isUser ? "user-message" : "ai-message"}
          >
            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
            {message.tokens_used && !isUser && (
              <p className="text-xs mt-2 opacity-70">
                {message.tokens_used.toLocaleString()} tokens used
              </p>
            )}
          </div>
          {isUser && (
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center order-3">
              <User className="h-4 w-4 text-primary-foreground" />
            </div>
          )}
        </div>
        
        {/* Feedback buttons for AI messages */}
        {!isUser && message.query_id && (
          <div className="mt-2 ml-10 flex items-center gap-2">
            {feedbackGiven ? (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                {feedbackGiven === "positive" ? (
                  <><ThumbsUp className="h-3 w-3 text-green-500" /> Thanks for the feedback!</>
                ) : (
                  <><ThumbsDown className="h-3 w-3 text-red-500" /> Thanks, we'll improve!</>
                )}
              </div>
            ) : (
              <>
                <span className="text-xs text-muted-foreground">Was this helpful?</span>
                <button
                  onClick={() => handleFeedback("positive")}
                  disabled={isSubmittingFeedback}
                  className="p-1 hover:bg-green-100 dark:hover:bg-green-900/30 rounded transition-colors disabled:opacity-50"
                  data-testid="feedback-positive"
                >
                  <ThumbsUp className="h-4 w-4 text-muted-foreground hover:text-green-500" />
                </button>
                <button
                  onClick={() => handleFeedback("negative")}
                  disabled={isSubmittingFeedback}
                  className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors disabled:opacity-50"
                  data-testid="feedback-negative"
                >
                  <ThumbsDown className="h-4 w-4 text-muted-foreground hover:text-red-500" />
                </button>
              </>
            )}
          </div>
        )}
        
        {/* Sources */}
        {message.sources && message.sources.length > 0 && (
          <div className="mt-2 ml-10">
            <p className="text-xs text-muted-foreground mb-1">Sources used:</p>
            <div className="flex flex-wrap gap-1">
              {message.sources.map((source, idx) => (
                <Badge key={idx} variant="outline" className="text-xs">
                  {source.type === "user_memory" && "Memory"}
                  {source.type === "user_objective" && "Objective"}
                  {source.type === "admin_kb" && "Knowledge Base"}
                  {source.type === "transaction" && "Transaction"}
                  {source.type === "web_search" && "Web Search"}
                  {source.relevance && ` (${Math.round(source.relevance * 100)}%)`}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Thinking animation component
const ThinkingAnimation = ({ steps, isComplete }) => {
  return (
    <div className="flex justify-start mb-4">
      <div className="max-w-[80%]">
        <div className="flex items-start gap-2">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <Brain className={`h-4 w-4 text-primary ${!isComplete ? "animate-pulse" : ""}`} />
          </div>
          <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium">Thinking</span>
              {!isComplete && <Loader2 className="h-3 w-3 animate-spin text-primary" />}
              {isComplete && <CheckCircle className="h-3 w-3 text-green-500" />}
            </div>
            <div className="space-y-2">
              {steps.map((step, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground animate-in fade-in slide-in-from-left-2 duration-300" style={{ animationDelay: `${idx * 100}ms` }}>
                  <ThinkingStepIcon step={step} />
                  <span>{step}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Usage stats component
const UsageStats = ({ usage, isLoading }) => {
  if (isLoading) {
    return (
      <div className="p-4 border-t">
        <Skeleton className="h-4 w-32 mb-2" />
        <Skeleton className="h-2 w-full" />
      </div>
    );
  }

  if (!usage) return null;

  const openrouterUsage = usage.usage_details?.openrouter || {};
  const percentage = openrouterUsage.percentage || 0;
  const isUnlimited = openrouterUsage.unlimited;

  return (
    <div className="p-4 border-t bg-muted/30">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Zap className="h-4 w-4" />
          <span>Token Usage</span>
        </div>
        <span className="text-sm font-medium">
          {isUnlimited ? (
            <Badge variant="outline" className="text-xs">Unlimited</Badge>
          ) : (
            `${openrouterUsage.used?.toLocaleString() || 0} / ${openrouterUsage.limit?.toLocaleString() || 0}`
          )}
        </span>
      </div>
      {!isUnlimited && (
        <Progress 
          value={percentage} 
          className="h-2" 
          data-testid="token-usage-progress"
        />
      )}
      {!isUnlimited && percentage > 80 && (
        <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          {percentage >= 100 ? "Token limit reached" : "Approaching token limit"}
        </p>
      )}
    </div>
  );
};

// Quick suggestions
const QuickSuggestions = ({ onSelect }) => {
  const suggestions = [
    { icon: TrendingUp, text: "How can I reduce my monthly expenses?" },
    { icon: Target, text: "What's my progress towards my savings goals?" },
    { icon: FileText, text: "Summarize my spending this month" },
    { icon: Sparkles, text: "Give me personalized financial advice" }
  ];

  return (
    <div className="p-4">
      <p className="text-sm text-muted-foreground mb-3">Try asking:</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {suggestions.map((suggestion, idx) => (
          <Button
            key={idx}
            variant="outline"
            className="justify-start h-auto py-3 px-4 text-left"
            onClick={() => onSelect(suggestion.text)}
            data-testid={`suggestion-${idx}`}
          >
            <suggestion.icon className="h-4 w-4 mr-2 flex-shrink-0 text-primary" />
            <span className="text-sm">{suggestion.text}</span>
          </Button>
        ))}
      </div>
    </div>
  );
};

export default function AIChat() {
  const { user } = useAuth();
  const { brandName } = useBrand();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [thinkingSteps, setThinkingSteps] = useState([]);
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aiEnabled, setAiEnabled] = useState(true);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, thinkingSteps]);

  // Fetch usage on mount
  const fetchUsage = useCallback(async () => {
    setUsageLoading(true);
    try {
      const res = await api.get("/ai/usage");
      setUsage(res.data);
      setAiEnabled(res.data.ai_enabled);
    } catch (err) {
      console.error("Failed to fetch AI usage:", err);
      if (err.response?.status === 403) {
        setAiEnabled(false);
      }
    } finally {
      setUsageLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  // Send message
  const sendMessage = async (content) => {
    if (!content.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      role: "user",
      content: content.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);
    setThinkingSteps([]);
    setError(null);

    try {
      const response = await api.post("/ai/chat", {
        query: content.trim(),
        include_thinking: true
      });

      const aiMessage = {
        id: Date.now() + 1,
        role: "assistant",
        content: response.data.response,
        thinking_steps: response.data.thinking_steps,
        sources: response.data.sources_used,
        tokens_used: response.data.tokens_used,
        query_id: response.data.query_id,
        timestamp: new Date().toISOString()
      };

      // Show thinking steps animation
      if (response.data.thinking_steps?.length > 0) {
        for (let i = 0; i < response.data.thinking_steps.length; i++) {
          await new Promise(resolve => setTimeout(resolve, 200));
          setThinkingSteps(prev => [...prev, response.data.thinking_steps[i]]);
        }
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      setThinkingSteps([]);
      setMessages(prev => [...prev, aiMessage]);
      
      // Refresh usage after each message
      fetchUsage();
    } catch (err) {
      console.error("AI chat error:", err);
      
      if (err.response?.status === 403) {
        setError({
          type: "access_denied",
          message: err.response.data?.detail || "AI chat is not available in your current package."
        });
        setAiEnabled(false);
      } else if (err.response?.status === 429) {
        setError({
          type: "limit_exceeded",
          message: err.response.data?.detail || "You've reached your monthly token limit."
        });
      } else {
        setError({
          type: "error",
          message: err.response?.data?.detail || "Failed to get AI response. Please try again."
        });
      }
      
      setThinkingSteps([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  // Handle suggestion click
  const handleSuggestionClick = (text) => {
    sendMessage(text);
  };

  // Handle feedback submission
  const handleFeedback = async (queryId, rating) => {
    try {
      await api.post("/ai/feedback", {
        query_id: queryId,
        rating: rating
      });
      toast.success(rating === "positive" ? "Thanks for the positive feedback!" : "Thanks for the feedback, we'll improve!");
    } catch (err) {
      toast.error("Failed to submit feedback");
      throw err;
    }
  };

  // Handle keyboard shortcut
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-background" data-testid="ai-chat-page">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate("/dashboard")}
              data-testid="back-to-dashboard"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Bot className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="font-semibold">{brandName} AI Assistant</h1>
                <p className="text-xs text-muted-foreground">Your personal financial advisor</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => navigate("/settings")}
                    data-testid="ai-settings-button"
                  >
                    <Settings className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>AI Memory Settings</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>

      {/* AI Disabled Alert */}
      {!aiEnabled && !usageLoading && (
        <Alert variant="destructive" className="mx-4 mt-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>AI Chat Unavailable</AlertTitle>
          <AlertDescription>
            AI chat is not available in your current package. Please upgrade to access AI features.
          </AlertDescription>
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert 
          variant={error.type === "limit_exceeded" ? "default" : "destructive"} 
          className="mx-4 mt-4"
        >
          {error.type === "limit_exceeded" ? (
            <AlertCircle className="h-4 w-4" />
          ) : (
            <XCircle className="h-4 w-4" />
          )}
          <AlertTitle>
            {error.type === "limit_exceeded" ? "Token Limit Reached" : "Error"}
          </AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      )}

      {/* Chat Area */}
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="container mx-auto px-4 py-6 max-w-3xl">
            {messages.length === 0 && !isLoading ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Bot className="h-8 w-8 text-primary" />
                </div>
                <h2 className="text-xl font-semibold mb-2">How can I help you today?</h2>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  I can analyze your finances, help you budget, track goals, and provide personalized advice based on your data.
                </p>
                <QuickSuggestions onSelect={handleSuggestionClick} />
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    isUser={message.role === "user"}
                    onFeedback={handleFeedback}
                  />
                ))}
                
                {/* Thinking Animation */}
                {isLoading && thinkingSteps.length > 0 && (
                  <ThinkingAnimation steps={thinkingSteps} isComplete={false} />
                )}
                
                {/* Loading indicator when no thinking steps yet */}
                {isLoading && thinkingSteps.length === 0 && (
                  <div className="flex justify-start mb-4">
                    <div className="flex items-start gap-2">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Bot className="h-4 w-4 text-primary animate-pulse" />
                      </div>
                      <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin text-primary" />
                          <span className="text-sm text-muted-foreground">Processing your request...</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Input Area */}
      <div className="border-t bg-card">
        <UsageStats usage={usage} isLoading={usageLoading} />
        <div className="container mx-auto px-4 py-4 max-w-3xl">
          <form onSubmit={handleSubmit} className="flex items-center gap-2">
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={aiEnabled ? "Ask me about your finances..." : "AI chat is not available"}
              disabled={isLoading || !aiEnabled}
              className="flex-1"
              data-testid="chat-input"
            />
            <Button 
              type="submit" 
              disabled={isLoading || !inputValue.trim() || !aiEnabled}
              data-testid="send-button"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </form>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            AI responses are based on your financial data and may not be 100% accurate. Always verify important financial decisions.
          </p>
        </div>
      </div>
    </div>
  );
}
